import "@fontsource-variable/manrope";
import "@fontsource-variable/oxanium";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";
import { gsap } from "gsap";
import * as THREE from "three";

import { AmbientField } from "./ambientField.js";
import { CameraRig } from "./camera.js";
import { GestureController } from "./gestureController.js";
import { InteractionController } from "./interactionController.js";
import { MouseController } from "./mouseController.js";
import { PARTICLE_MODELS } from "./particleModels.js";
import { ParticleSystem } from "./particleSystem.js";
import { PostProcessing } from "./postprocessing.js";
import { QualityManager } from "./qualityManager.js";
import { UI } from "./UI.js";
import "./styles.css";

const app = document.querySelector("#app");
const canvas = document.querySelector("#webgl");
const ui = new UI(app);

let renderer;

try {
  renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    depth: true,
    powerPreference: "high-performance",
    stencil: false,
  });
} catch {
  ui.showWebGLError();
}

if (renderer) {
  startScene();
}

/**
 * Builds the particle scene and the opt-in stage-05 hand tracking pipeline.
 */
function startScene() {
  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(0x040612, 0.055);
  scene.add(new THREE.AmbientLight(0x8ebfff, 0.24));

  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.02;
  renderer.setClearColor(0x040612, 0);

  const cameraRig = new CameraRig(window.innerWidth / window.innerHeight);
  scene.add(cameraRig.root);

  const particleSystem = new ParticleSystem(scene, {
    count: 100_000,
    maxCount: 150_000,
  });
  const initialPalette = PARTICLE_MODELS.find(
    (model) => model.id === "saturn",
  );
  const ambientField = new AmbientField(scene, {
    colorA: initialPalette.primary,
    colorB: initialPalette.secondary,
  });
  const postProcessing = new PostProcessing(
    renderer,
    scene,
    cameraRig.camera,
  );
  const gestureController = new GestureController({
    maxInferenceFps: 24,
  });
  const interactionController = new InteractionController();
  const mouseController = new MouseController(canvas);
  const qualityManager = new QualityManager({
    isWebGL2: renderer.capabilities.isWebGL2,
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: navigator.deviceMemory,
    viewportWidth: window.innerWidth,
    requestedParticles: 100_000,
    startedAt: performance.now(),
  });

  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  particleSystem.setParticleSize(1.4);
  particleSystem.setGlow(1.25);
  particleSystem.setReducedMotion(reducedMotion);

  if (reducedMotion) {
    particleSystem.points.scale.setScalar(1);
    particleSystem.uniforms.uOpacity.value = 1;
  } else {
    gsap.fromTo(
      particleSystem.points.scale,
      { x: 0.82, y: 0.82, z: 0.82 },
      {
        x: 1,
        y: 1,
        z: 1,
        duration: 2.4,
        ease: "expo.out",
      },
    );
    gsap.fromTo(
      particleSystem.uniforms.uOpacity,
      { value: 0 },
      { value: 1, duration: 1.8, ease: "power2.out" },
    );
  }

  postProcessing.setGlow(1.25);
  const initialQuality = qualityManager.getSnapshot();
  particleSystem.setDensity(initialQuality.particleCount);
  postProcessing.setQuality(initialQuality);
  ui.setQuality(formatQualityLabel(initialQuality));
  ui.setModelState("Saturn");
  ui.reveal();

  let latestModelRequest = "saturn";
  let cameraRequestId = 0;
  const unsubscribeGesture = gestureController.subscribe((frame) => {
    interactionController.handleGestureFrame(frame);
    ui.updateHandFrame(frame);
  });

  ui.on("cameraToggle", async (enabled) => {
    const requestId = ++cameraRequestId;

    if (!enabled) {
      gestureController.stop();
      ui.setCameraState("off");
      return;
    }

    ui.setCameraState("loading", "Preparing model");

    try {
      await gestureController.start(ui.getCameraVideo());
      if (requestId !== cameraRequestId) {
        gestureController.stop();
        ui.setCameraState("off");
      }
    } catch (error) {
      if (requestId === cameraRequestId) {
        ui.setCameraState("error", cameraFailureMessage(error?.code));
      }
    }
  });
  ui.on("modelChange", async ({ id, label }) => {
    latestModelRequest = id;
    ui.setModelState(label, "morphing");
    const definition = PARTICLE_MODELS.find((model) => model.id === id);
    if (definition) {
      ui.setPalette(definition.primary, definition.secondary, {
        mode: "gradient",
      });
      ambientField.setPalette(definition.primary, definition.secondary);
    }

    try {
      await particleSystem.setModel(id);
      if (latestModelRequest === id) {
        ui.setModelState(label);
      }
    } catch {
      if (latestModelRequest === id) {
        ui.setStatus("Model generation failed", "error");
      }
    }
  });
  ui.on("densityChange", (value) => {
    particleSystem.setDensity(qualityManager.setRequestedParticles(value));
    ui.setQuality(formatQualityLabel(qualityManager.getSnapshot()));
  });
  ui.on("particleSizeChange", (value) =>
    particleSystem.setParticleSize(value),
  );
  ui.on("glowChange", (value) => {
    particleSystem.setGlow(value);
    postProcessing.setGlow(value);
  });
  ui.on("rotationSensitivityChange", (value) => {
    interactionController.setRotationSensitivity(value);
    mouseController.setRotationSensitivity(value);
  });
  ui.on("colorChange", ({ mode, primary, secondary }) => {
    const colorB = mode === "single" ? primary : secondary;
    particleSystem.setColorMode(primary, colorB);
    ambientField.setPalette(primary, colorB);
  });

  const timer = new THREE.Timer();
  timer.connect(document);
  let elapsed = 0;
  let animationFrame = 0;
  let fpsFrames = 0;
  let fpsSampleStarted = performance.now();

  const resize = () => {
    const width = Math.max(app.clientWidth, 1);
    const height = Math.max(app.clientHeight, 1);
    const pixelRatio = qualityManager.getPixelRatio(
      window.devicePixelRatio,
    );

    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(width, height, false);
    cameraRig.resize(width, height);
    particleSystem.setPixelRatio(pixelRatio);
    ambientField.setPixelRatio(pixelRatio);
    postProcessing.setSize(width, height, pixelRatio);
  };

  const render = (now) => {
    timer.update(now);
    const delta = Math.min(timer.getDelta(), 0.05);
    elapsed += delta;

    const interaction = interactionController.update(delta);
    const mouseInteraction = mouseController.update(delta, interaction);
    particleSystem.setInteraction(interaction.particles);
    particleSystem.setRotation(
      mouseInteraction.rotation.x,
      mouseInteraction.rotation.y,
    );
    cameraRig.setPointer(
      mouseInteraction.pointer.x,
      mouseInteraction.pointer.y,
    );
    cameraRig.setDistance(mouseInteraction.camera.distance);
    ui.updateInteractionState(interaction);
    cameraRig.update(delta);
    postProcessing.setFocus(cameraRig.camera.position.z);
    particleSystem.update(elapsed, delta);
    ambientField.update(elapsed);
    postProcessing.render(delta);

    fpsFrames += 1;
    const fpsWindow = now - fpsSampleStarted;
    if (fpsWindow >= 600) {
      const fps = (fpsFrames * 1000) / fpsWindow;
      ui.setFps(fps);
      const qualityChange = qualityManager.sample(fps, now);
      if (qualityChange) {
        particleSystem.setDensity(qualityChange.particleCount);
        postProcessing.setQuality(qualityChange);
        ui.setQuality(formatQualityLabel(qualityChange));
        resize();
      }
      fpsFrames = 0;
      fpsSampleStarted = now;
    }

    animationFrame = requestAnimationFrame(render);
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      cancelAnimationFrame(animationFrame);
      animationFrame = 0;
      return;
    }

    if (!animationFrame) {
      timer.reset();
      qualityManager.resetSampling(performance.now());
      fpsFrames = 0;
      fpsSampleStarted = performance.now();
      animationFrame = requestAnimationFrame(render);
    }
  };

  const dispose = () => {
    cancelAnimationFrame(animationFrame);
    window.removeEventListener("resize", resize);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    postProcessing.dispose();
    ambientField.dispose();
    particleSystem.dispose();
    unsubscribeGesture();
    gestureController.dispose();
    mouseController.dispose();
    timer.dispose();
    ui.dispose();
    renderer.dispose();
  };

  window.addEventListener("resize", resize, { passive: true });
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("beforeunload", dispose, { once: true });

  resize();
  animationFrame = requestAnimationFrame(render);
}

function formatQualityLabel(profile) {
  const webgl = renderer.capabilities.isWebGL2 ? "WGL2" : "WGL1";
  return profile.label + " · " + webgl;
}

function cameraFailureMessage(code) {
  const messages = {
    "permission-denied": "Permission denied",
    "camera-unavailable": "No camera",
    "camera-busy": "Camera busy",
    "camera-constraints": "Unsupported camera",
    "insecure-context": "HTTPS required",
    "model-load-failed": "Model unavailable",
  };
  return messages[code] ?? "Camera unavailable";
}
